import styled from 'styled-components';
import { media } from '@bitcoin-portal/bitcoincom-pkg-theme';
import Section from '@bitcoin-portal/bitcoincom-pkg-components/dist/Section';
import { LazyLoadImage } from 'react-lazy-load-image-component';

interface HideableProps {
  $hidden?: boolean;
}

export const StyledSection = styled(Section)`
  background: #ffffff !important;
  padding: 40px 0px !important;
  margin: 0px !important;

  ${media.xl`
    padding: 100px 0px !important;
  `}
`;

export const StyledContentBlock = styled.div`
  max-width: 1440px;
  padding: 0px !important;
  margin: auto;
`;

export const MainContainer = styled.div`
  padding: 0px 20px !important;

  ${media.md`
    padding: 0px 60px !important;
  `}

  ${media.xl`
    padding: 0px 80px !important;
  `}
`;

export const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  /* Reserve minimum height to prevent CLS during font loading */
  min-height: 70px;

  ${media.md`
    min-height: 120px;
  `}

  ${media.xl`
    min-height: 180px;
  `}
`;

export const Title = styled.h1`
  color: #1c1c1c;
  font-family: 'Elza Condensed';
  font-size: 28px;
  font-weight: 500;
  line-height: 85%;
  letter-spacing: -0.56px;
  text-align: center;
  text-transform: uppercase;
  margin: 0px;
  /* Reserve minimum height to prevent CLS */
  min-height: 24px;

  ${media.md`
    font-size: 70px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -1.4px;
    min-height: 60px;
  `}

  ${media.xl`
    font-size: 110px;
    font-weight: 500;
    line-height: 85%;
    letter-spacing: -2.2px;
    min-height: 94px;
  `}
`;

export const Text = styled.p`
  color: #67656e;
  font-family: 'Satoshi Variable Medium';
  font-size: 16px;
  font-weight: 500;
  line-height: 135%;
  letter-spacing: -0.32px;
  text-align: center;
  margin: 0px;
  /* Reserve minimum height to prevent CLS during font loading */
  min-height: 22px;

  ${media.md`
    font-size: 22px;
    font-weight: 500;
    line-height: 123%;
    letter-spacing: -0.48px;
    min-height: 27px;
  `}

  ${media.xl`
    font-size: 28px;
    font-weight: 500;
    line-height: 123%;
    letter-spacing: -0.48px;
    min-height: 35px;
  `}
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 32px;
  margin: 24px 0px 0px;

  ${media.md`
    flex-direction: row;
    gap: 60px;
    margin: 40px 0px 0px;
  `}

  ${media.xl`
    gap: 120px;
    margin: 50px 0px 0px;
  `}

  iframe {
    border: none;
  }
`;

export const ImageSection = styled.div`
  display: none;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  /* Fixed aspect ratio container to prevent CLS */
  aspect-ratio: 550 / 744;
  max-width: 550px;

  ${media.md`
    display: flex;
    width: calc(50% - 30px);
    max-width: 550px;
  `}

  ${media.lg`
    display: flex;
    aspect-ratio: 1 / 1;
    max-width: 650px;
    max-height: 650px;
  `}

  ${media.xl`
    display: flex;
    width: calc(50% - 60px);
    max-width: 650px;
  `}
`;

export const Image = styled(LazyLoadImage)<HideableProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${props => (props.$hidden ? 0 : 1)};
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
`;

export const Video = styled.video<HideableProps>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  opacity: ${props => (props.$hidden ? 0 : 1)};
  visibility: ${props => (props.$hidden ? 'hidden' : 'visible')};
  transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
`;

export const ContentSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  /* Reserve minimum height to prevent CLS when widgets load */
  min-height: 480px;

  ${media.md`
    width: calc(50% - 30px);
  `}

  ${media.lg`
    min-height: 610px;
  `}

  ${media.xl`
    width: calc(50% - 60px);
    min-height: 650px;
  `}
`;

export const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  /* Match widget dimensions to prevent CLS when transitioning to actual widget */
  height: 480px;
  max-width: 480px;
  background: #f0f0f5;
  border-radius: 24px;

  ${media.lg`
    height: 610px;
  `}

  ${media.xl`
    height: 650px;
  `}
`;
